import { useEffect, useRef, useState, useCallback } from "react";
import deepgramTokenService from "./DeepgramTokenService.js";
/**
 * Deepgram Real-time Speech-to-Text Hook
 * Provides real-time transcription using Deepgram WebSocket API
 * Works alongside existing Ultravox/LiveAvatar implementation
 */
export function useDeepgramSTT({
  useTokenAuth = true, // Use token-based authentication by default
  model = "nova-2",
  language = "en-US",
  smartFormat = true,
  interimResults = true,
  endpointing = 300,
  vadEvents = true,
  autoStart = false,
  maxReconnectAttempts = 5,
  micStream = null, // External microphone stream
  audioBitsPerSecond = 16000, // Opus bitrate for the MediaRecorder; raise for accuracy-sensitive callers
  onInterimTranscript,
  onFinalTranscript,
  onError,
} = {}) {
  const [isConnected, setIsConnected] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [error, setError] = useState(null);
  const [interimTranscript, setInterimTranscript] = useState("");
  const [finalTranscript, setFinalTranscript] = useState("");

  const socketRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const audioFormatRef = useRef(null);

  // Deepgram WebSocket URL
  const getWebSocketUrl = useCallback(() => {
    // Determine encoding based on audio format
    const params = new URLSearchParams({
      model,
      language,
      smart_format: smartFormat,
      interim_results: interimResults,
      endpointing,
      vad_events: vadEvents,
      encoding: "opus",
      // sample_rate: 48000,
      // channels: 2
    });

    return `wss://api.deepgram.com/v1/listen?${params.toString()}`;
  }, [model, language, smartFormat, interimResults, endpointing, vadEvents]);

  // Initialize WebSocket connection
  const connectWebSocket = useCallback(async () => {
    try {
      let authToken;

      // Get token from server if using token auth
      if (useTokenAuth) {
        console.log("[Deepgram] Fetching token from server...");
        authToken = await deepgramTokenService.getToken();
        console.log("[Deepgram] Token:", authToken);
      }

      if (!authToken) {
        const errorMsg = useTokenAuth
          ? "Failed to get Deepgram token"
          : "Deepgram API key is required";
        setError(errorMsg);
        onError?.(errorMsg);
        return;
      }

      console.log("[Deepgram] Initiating WebSocket connection...");
      const wsUrl = getWebSocketUrl();
      console.log("[Deepgram] WebSocket URL:", wsUrl);
      socketRef.current = new WebSocket(wsUrl, ["bearer", authToken]);

      socketRef.current.onopen = () => {
        console.log("[Deepgram] WebSocket connected");
        setIsConnected(true);
        setError(null);
        reconnectAttemptsRef.current = 0;
      };

      socketRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.type === "Results") {
            const transcript = data.channel?.alternatives?.[0]?.transcript;
            const isFinal = data.is_final;

            if (transcript) {
              if (isFinal) {
                setFinalTranscript(transcript); // Replace rather than append for real-time use
                setInterimTranscript(""); // Clear interim when we get final
                onFinalTranscript?.(transcript);
              } else {
                setInterimTranscript(transcript);
                onInterimTranscript?.(transcript);
              }
            }
          } else if (data.type === "Metadata") {
            console.log("[Deepgram] Metadata:", data);
          } else if (data.type === "SpeechStarted") {
            console.log("[Deepgram] Speech started");
          } else if (data.type === "UtteranceEnd") {
            console.log("[Deepgram] Utterance ended");
            setInterimTranscript(""); // Clear interim on utterance end
          }
        } catch (err) {
          console.error("[Deepgram] Error parsing message:", err);
        }
      };

      socketRef.current.onerror = (error) => {
        console.error("[Deepgram] WebSocket error:", error);
        const errorMsg = "WebSocket connection error";
        setError(errorMsg);
        onError?.(errorMsg);
      };

      socketRef.current.onclose = (event) => {
        console.log("[Deepgram] WebSocket closed:", event.code, event.reason);
        setIsConnected(false);
        setIsListening(false);

        // Attempt to reconnect if it wasn't a clean close
        if (
          event.code !== 1000 &&
          reconnectAttemptsRef.current < maxReconnectAttempts
        ) {
          const delay = Math.pow(2, reconnectAttemptsRef.current) * 1000; // Exponential backoff
          console.log(
            `[Deepgram] Attempting to reconnect in ${delay}ms (attempt ${
              reconnectAttemptsRef.current + 1
            }/${maxReconnectAttempts})`,
          );

          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttemptsRef.current++;
            connectWebSocket();
          }, delay);
        }
      };
    } catch (err) {
      console.error("[Deepgram] Error creating WebSocket:", err);
      const errorMsg = "Failed to create WebSocket connection";
      setError(errorMsg);
      onError?.(errorMsg);
    }
  }, [
    useTokenAuth,
    getWebSocketUrl,
    onError,
    onFinalTranscript,
    onInterimTranscript,
  ]);

  // Start listening
  const startListening = useCallback(async () => {
    console.log("[Deepgram] startListening called, isListening:", isListening);
    if (isListening) return;

    try {
      // Use external microphone stream if provided, otherwise request new one
      let stream;
      if (micStream) {
        console.log("[Deepgram] Using external microphone stream");
        stream = micStream;
      } else {
        console.log("[Deepgram] Requesting new microphone stream");
        stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        });
      }

      streamRef.current = stream;

      // Connect WebSocket if we don't already have a genuinely open one.
      // Reading socketRef.current's live readyState instead of the
      // `isConnected` state avoids a stale-closure race: state updates from
      // a disconnect()/close() called earlier in the same synchronous tick
      // (e.g. MicCheck's retry flow calling disconnect() then immediately
      // startListening()) aren't reflected in this closure's `isConnected`
      // yet, but the ref is always current. This also fixes the underlying
      // bug on its own: stopListening() never closes/nulls the socket (only
      // the unmount cleanup does), so `isConnected` can stay stale-true long
      // after the server has actually closed the connection following a
      // prior CloseStream - reading the live readyState here is correct
      // regardless of whether React's isConnected state has caught up.
      if (socketRef.current?.readyState !== WebSocket.OPEN) {
        connectWebSocket();
        // Wait for connection. Bounded with a timeout - if connectWebSocket
        // bailed early (e.g. token fetch failed) socketRef.current is never
        // assigned, and an unbounded version of this loop would poll forever,
        // leaving the caller hung on "Connecting..." with no way to fail over.
        await new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            reject(new Error("WebSocket connection timeout"));
          }, 10000);

          const checkConnection = () => {
            if (socketRef.current?.readyState === WebSocket.OPEN) {
              clearTimeout(timeout);
              resolve();
            } else if (socketRef.current?.readyState === WebSocket.CLOSED) {
              clearTimeout(timeout);
              reject(new Error("WebSocket connection failed"));
            } else {
              setTimeout(checkConnection, 100);
            }
          };
          checkConnection();
        });
      }
      audioFormatRef.current = "audio/webm;codecs=opus";

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: "audio/webm;codecs=opus",
        audioBitsPerSecond,
      });

      mediaRecorder.ondataavailable = async (event) => {
        if (
          event.data.size > 0 &&
          socketRef.current?.readyState === WebSocket.OPEN
        ) {
          try {
            // Send WebM/Opus format directly - Deepgram supports this natively
            const buffer = await event.data.arrayBuffer();
            socketRef.current.send(buffer);
          } catch (error) {
            console.error("[Deepgram] Error processing audio data:", error);
          }
        }
      };

      mediaRecorder.onerror = (event) => {
        console.error("[Deepgram] MediaRecorder error:", event.error);
        setError(event.error);
        onError?.(event.error);
      };

      // Start recording with small time slices for real-time streaming
      mediaRecorder.start(250); // 250ms chunks for real-time processing
      mediaRecorderRef.current = mediaRecorder;

      setIsListening(true);
      setError(null);
    } catch (err) {
      console.error("[Deepgram] Error starting listening:", err);
      const errorMsg = "Failed to start listening: " + err.message;
      setError(errorMsg);
      onError?.(errorMsg);
      // Let the caller know this attempt did not actually succeed - without
      // this, `await startListening()` resolved normally even on failure,
      // so callers like MicCheck would proceed straight to "listening" with
      // no audio ever actually being sent. The one internal call site that
      // doesn't await/catch this (the autoStart effect below) is wrapped in
      // its own .catch() to preserve today's fire-and-forget behavior there.
      throw err;
    }
  }, [isListening, connectWebSocket, onError, micStream, audioBitsPerSecond]);

  // Stop listening
  const stopListening = useCallback(() => {
    // Deliberately does NOT gate on the `isListening` state - only on the
    // live refs checked individually below. A closure that captured a
    // stale, pre-attempt `isListening=false` must still be able to
    // actually stop what's really running when it's eventually invoked -
    // e.g. MicCheck's MAX_RECORD_MS hard-cap timer, which is armed via
    // `setTimeout(stopRecording, MAX_RECORD_MS)` synchronously right after
    // startListening() resolves, but *before* React has re-rendered to
    // hand out a fresh closure reflecting this attempt's isListening=true.
    // The old `if (!isListening) return;` guard silently no-op'd in
    // exactly that case: setIsListening(false) never ran, so isListening
    // stayed stuck `true` forever, which then made every later
    // startListening() call's own `if (isListening) return` guard bail out
    // immediately - no connection, no MediaRecorder, no audio ever sent
    // again, until a full page reload reset all React state. Every
    // operation below is already individually guarded, so running them
    // unconditionally is safe.
    try {
      // Stop MediaRecorder
      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state !== "inactive"
      ) {
        mediaRecorderRef.current.stop();
        mediaRecorderRef.current = null;
      }

      // Stop media stream only if we created it ourselves (not external stream)
      if (streamRef.current && !micStream) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
      streamRef.current = null;

      // Send close message to Deepgram
      if (socketRef.current?.readyState === WebSocket.OPEN) {
        socketRef.current.send(JSON.stringify({ type: "CloseStream" }));
      }

      setIsListening(false);
      setInterimTranscript("");
    } catch (err) {
      console.error("[Deepgram] Error stopping listening:", err);
    }
  }, [micStream]);

  // Fully tear down the current WebSocket connection, unlike stopListening
  // (which only sends CloseStream and leaves the socket object/isConnected
  // alone - by design, since Conversation-v2.js's pause/resume mute flow
  // wants the connection kept alive). Callers that run multiple independent
  // "recording attempts" on one hook instance (e.g. MicCheck's retries) need
  // this to guarantee the *next* attempt gets a genuinely fresh connection
  // instead of reusing one Deepgram already considers closing. Mirrors the
  // unmount cleanup below, just made callable on demand.
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (socketRef.current) {
      if (socketRef.current.readyState === WebSocket.OPEN) {
        try {
          socketRef.current.send(JSON.stringify({ type: "CloseStream" }));
        } catch (e) {
          // Best-effort - we're tearing this connection down regardless.
        }
      }
      socketRef.current.close(1000, "Client requested disconnect");
      socketRef.current = null;
    }
    setIsConnected(false);
  }, []);

  // Reset transcripts
  const resetTranscripts = useCallback(() => {
    setInterimTranscript("");
    setFinalTranscript("");
  }, []);

  // Auto-start if enabled
  useEffect(() => {
    console.log("AUTO : autoStart : ", autoStart);
    console.log("AUTO : isListening : ", isListening);
    if (autoStart) {
      // startListening() now rejects on failure (see its catch block) so
      // real callers can react to it - this fire-and-forget site doesn't
      // await it, so catch here to preserve the previous "log and move on"
      // behavior instead of surfacing an unhandled promise rejection.
      startListening().catch(() => {});
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    console.log("[Deepgram] Hook mounted");
    return () => {
      console.log("[Deepgram] Hook unmounting - cleaning up");
      // Stop audio processing
      if (mediaRecorderRef.current) {
        const { audioContext, source, processor } = mediaRecorderRef.current;
        if (processor && source) {
          try {
            source.disconnect(processor);
            processor.disconnect();
          } catch (e) {
            // Ignore disconnect errors during cleanup
          }
        }
        if (audioContext && audioContext.state !== "closed") {
          audioContext.close().catch(() => {
            // Ignore close errors during cleanup
          });
        }
        mediaRecorderRef.current = null;
      }

      // Stop media stream only if we created it ourselves (not external stream)
      if (streamRef.current && !micStream) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
      streamRef.current = null;

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }

      if (socketRef.current) {
        if (socketRef.current.readyState === WebSocket.OPEN) {
          socketRef.current.send(JSON.stringify({ type: "CloseStream" }));
        }
        socketRef.current.close(1000, "Component unmounting");
        socketRef.current = null;
      }
    };
  }, []);

  // Pause MediaRecorder (keep WebSocket connection alive)
  const pauseRecording = useCallback(() => {
    if (!isListening || isPaused) return;

    try {
      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state === "recording"
      ) {
        console.log("[Deepgram] Pausing MediaRecorder");
        // mediaRecorderRef.current.pause();
        setIsPaused(true);
      }
    } catch (err) {
      console.error("[Deepgram] Error pausing MediaRecorder:", err);
    }
  }, [isListening, isPaused]);

  // Resume MediaRecorder
  const resumeRecording = useCallback(() => {
    if (!isListening || !isPaused) return;

    try {
      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state === "paused"
      ) {
        console.log("[Deepgram] Resuming MediaRecorder");
        // mediaRecorderRef.current.resume();
        setIsPaused(false);
      }
    } catch (err) {
      console.error("[Deepgram] Error resuming MediaRecorder:", err);
    }
  }, [isListening, isPaused]);

  return {
    isConnected,
    isListening,
    isPaused,
    error,
    interimTranscript,
    finalTranscript,
    startListening,
    stopListening,
    disconnect,
    pauseRecording,
    resumeRecording,
    resetTranscripts,
  };
}

/**
 * Higher-level hook that integrates with existing conversation flow
 * Provides real-time transcripts while preserving Ultravox final transcripts
 */
export function useDeepgramIntegration({
  onRealtimeTranscript,
  ...deepgramOptions
} = {}) {
  const [realtimeTranscript, setRealtimeTranscript] = useState("");

  const deepgram = useDeepgramSTT({
    ...deepgramOptions,
    onInterimTranscript: (transcript) => {
      setRealtimeTranscript(transcript);
      onRealtimeTranscript?.(transcript, false); // false = interim
    },
    onFinalTranscript: (transcript) => {
      setRealtimeTranscript(transcript);
      onRealtimeTranscript?.(transcript, true); // true = final
    },
  });

  return {
    ...deepgram,
    realtimeTranscript,
    pauseRecording: deepgram.pauseRecording,
    resumeRecording: deepgram.resumeRecording,
  };
}

/**
 * Deepgram Real-time Speech-to-Text Hook for Avatar/Agent Audio
 * Extracts audio from a MediaStream (e.g., avatar video stream) and provides real-time transcription
 * Independent of the user microphone STT integration
 */
let canStartListening = true;
export function useDeepgramAvatarSTT({
  useTokenAuth = true,
  model = "nova-2",
  language = "en-US",
  smartFormat = true,
  interimResults = true,
  endpointing = 300,
  vadEvents = true,
  autoStart = false,
  maxReconnectAttempts = 5,
  avatarMediaStream = null, // Avatar/agent media stream containing audio
  onInterimTranscript,
  onFinalTranscript,
  onError,
  debugLogging = false,
} = {}) {
  const [isConnected, setIsConnected] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [error, setError] = useState(null);
  const [interimTranscript, setInterimTranscript] = useState("");
  const [finalTranscript, setFinalTranscript] = useState("");

  const socketRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioStreamRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const isCleaningUpRef = useRef(false);

  const log = useCallback(
    (...args) => {
      if (debugLogging) {
        console.log("[Deepgram Avatar]", ...args);
      }
    },
    [debugLogging],
  );

  // Deepgram WebSocket URL
  const getWebSocketUrl = useCallback(() => {
    const params = new URLSearchParams({
      model,
      language,
      smart_format: smartFormat,
      interim_results: interimResults,
      endpointing,
      vad_events: vadEvents,
      encoding: "opus",
    });

    return `wss://api.deepgram.com/v1/listen?${params.toString()}`;
  }, [model, language, smartFormat, interimResults, endpointing, vadEvents]);

  // Initialize WebSocket connection
  const connectWebSocket = useCallback(async () => {
    if (isCleaningUpRef.current) return;

    try {
      let authToken;

      if (useTokenAuth) {
        log("Fetching token from server...");
        authToken = await deepgramTokenService.getToken();
        log("Token received");
      }

      if (!authToken) {
        const errorMsg = useTokenAuth
          ? "Failed to get Deepgram token"
          : "Deepgram API key is required";
        setError(errorMsg);
        onError?.(errorMsg);
        return;
      }

      log("Initiating WebSocket connection...");
      const wsUrl = getWebSocketUrl();
      log("WebSocket URL:", wsUrl);
      socketRef.current = new WebSocket(wsUrl, ["bearer", authToken]);

      socketRef.current.onopen = () => {
        log("WebSocket connected");
        setIsConnected(true);
        setError(null);
        reconnectAttemptsRef.current = 0;
      };

      socketRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.type === "Results") {
            const transcript = data.channel?.alternatives?.[0]?.transcript;
            const isFinal = data.is_final;

            if (transcript) {
              log("Transcript:", transcript, "Final:", isFinal);
              if (isFinal) {
                setFinalTranscript(transcript);
                setInterimTranscript("");
                onFinalTranscript?.(transcript);
              } else {
                setInterimTranscript(transcript);
                onInterimTranscript?.(transcript);
              }
            }
          } else if (data.type === "Metadata") {
            log("Metadata:", data);
          } else if (data.type === "SpeechStarted") {
            log("Speech started");
          } else if (data.type === "UtteranceEnd") {
            log("Utterance ended");
            setInterimTranscript("");
          }
        } catch (err) {
          console.error("[Deepgram Avatar] Error parsing message:", err);
        }
      };

      socketRef.current.onerror = (error) => {
        console.error("[Deepgram Avatar] WebSocket error:", error);
        const errorMsg = "WebSocket connection error";
        setError(errorMsg);
        onError?.(errorMsg);
      };

      socketRef.current.onclose = (event) => {
        log("WebSocket closed:", event.code, event.reason);
        setIsConnected(false);
        setIsListening(false);

        if (
          !isCleaningUpRef.current &&
          event.code !== 1000 &&
          reconnectAttemptsRef.current < maxReconnectAttempts
        ) {
          const delay = Math.pow(2, reconnectAttemptsRef.current) * 1000;
          log(
            `Attempting to reconnect in ${delay}ms (attempt ${
              reconnectAttemptsRef.current + 1
            }/${maxReconnectAttempts})`,
          );

          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttemptsRef.current++;
            connectWebSocket();
          }, delay);
        }
      };
    } catch (err) {
      console.error("[Deepgram Avatar] Error creating WebSocket:", err);
      const errorMsg = "Failed to create WebSocket connection";
      setError(errorMsg);
      onError?.(errorMsg);
    }
  }, [
    useTokenAuth,
    getWebSocketUrl,
    onError,
    onFinalTranscript,
    onInterimTranscript,
    maxReconnectAttempts,
    log,
  ]);

  // Extract audio stream from avatar media stream
  const extractAudioStream = useCallback((mediaStream) => {
    if (!mediaStream) return null;

    const audioTracks = mediaStream.getAudioTracks();
    if (audioTracks.length === 0) {
      console.warn("[Deepgram Avatar] No audio tracks found in media stream");
      return null;
    }

    // Create a new MediaStream with only audio tracks
    const audioStream = new MediaStream(audioTracks);
    return audioStream;
  }, []);

  // Start listening to avatar audio
  const startListening = useCallback(async () => {
    log("startListening called, isListening:", isListening);
    if (isListening) return false;

    if (!avatarMediaStream) {
      const errorMsg = "Avatar media stream is not available";
      log(errorMsg);
      setError(errorMsg);
      onError?.(errorMsg);
      return false;
    }

    try {
      // Extract audio from avatar media stream
      const audioStream = extractAudioStream(avatarMediaStream);
      if (!audioStream) {
        const errorMsg = "Failed to extract audio from avatar media stream";
        setError(errorMsg);
        onError?.(errorMsg);
        return false;
      }

      audioStreamRef.current = audioStream;
      log(`Audio stream extracted from avatar media stream : ${audioStream}`);

      // Connect WebSocket if not connected
      if (!isConnected || !socketRef.current) {
        connectWebSocket();
        // Wait for connection
        await new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            reject(new Error("WebSocket connection timeout"));
          }, 10000);

          const checkConnection = () => {
            if (socketRef.current?.readyState === WebSocket.OPEN) {
              clearTimeout(timeout);
              resolve();
            } else if (socketRef.current?.readyState === WebSocket.CLOSED) {
              clearTimeout(timeout);
              reject(new Error("WebSocket connection failed"));
            } else {
              setTimeout(checkConnection, 100);
            }
          };
          checkConnection();
        });
      }

      // Create MediaRecorder for audio stream
      console.log(
        "[Deepgram Avatar] audioStream tracks: ",
        audioStream.getTracks(),
      );
      const mediaRecorder = new MediaRecorder(audioStream, {
        mimeType: "audio/webm;codecs=opus",
        audioBitsPerSecond: 16000,
      });

      mediaRecorder.ondataavailable = async (event) => {
        if (
          event.data.size > 0 &&
          socketRef.current?.readyState === WebSocket.OPEN
        ) {
          try {
            const buffer = await event.data.arrayBuffer();
            socketRef.current.send(buffer);
          } catch (error) {
            console.error(
              "[Deepgram Avatar] Error processing audio data:",
              error,
            );
          }
        }
      };

      mediaRecorder.onerror = (event) => {
        console.error("[Deepgram Avatar] MediaRecorder error:", event.error);
        setError(event.error);
        onError?.(event.error);
      };

      // Start recording with small time slices for real-time streaming
      mediaRecorder.start(250); // 250ms chunks
      mediaRecorderRef.current = mediaRecorder;

      setIsListening(true);
      setError(null);
      log("Started listening to avatar audio");
      return true;
    } catch (err) {
      console.error("[Deepgram Avatar] Error starting listening:", err);
      const errorMsg = "Failed to start listening: " + err.message;
      setError(errorMsg);
      onError?.(errorMsg);
      return false;
    }
  }, [
    isListening,
    isConnected,
    avatarMediaStream,
    connectWebSocket,
    extractAudioStream,
    onError,
    log,
  ]);

  // Stop listening
  const stopListening = useCallback(() => {
    if (!isListening) return;

    log("Stopping listening...");

    try {
      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state !== "inactive"
      ) {
        mediaRecorderRef.current.stop();
        mediaRecorderRef.current = null;
      }

      // Don't stop the audio stream tracks as they belong to the avatar media stream
      audioStreamRef.current = null;

      if (socketRef.current?.readyState === WebSocket.OPEN) {
        socketRef.current.send(JSON.stringify({ type: "CloseStream" }));
      }

      setIsListening(false);
      setInterimTranscript("");
      log("Stopped listening");
    } catch (err) {
      console.error("[Deepgram Avatar] Error stopping listening:", err);
    }
  }, [isListening, log]);

  // Reset transcripts
  const resetTranscripts = useCallback(() => {
    setInterimTranscript("");
    setFinalTranscript("");
  }, []);

  // Auto-start when avatar media stream becomes available
  useEffect(() => {
    if (autoStart && avatarMediaStream && !isListening && canStartListening) {
      log("Auto-starting with avatar media stream");
      canStartListening = false;
      startListening()
        .then((result) => {
          if (!result) {
            canStartListening = true;
          }
        })
        .catch((e) => {
          console.error("[Deepgram Avatar] Error starting listening:", e);
          canStartListening = true;
        });
      // isProcessing = false;
    }
  }, [autoStart, avatarMediaStream, isListening, startListening, log]);

  // Cleanup on unmount
  useEffect(() => {
    log("Hook mounted");
    return () => {
      log("Hook unmounting - cleaning up");
      isCleaningUpRef.current = true;

      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state !== "inactive"
      ) {
        try {
          mediaRecorderRef.current.stop();
        } catch (e) {
          // Ignore errors during cleanup
        }
        mediaRecorderRef.current = null;
      }

      audioStreamRef.current = null;

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }

      if (socketRef.current) {
        if (socketRef.current.readyState === WebSocket.OPEN) {
          socketRef.current.send(JSON.stringify({ type: "CloseStream" }));
        }
        socketRef.current.close(1000, "Component unmounting");
        socketRef.current = null;
      }
    };
  }, [log]);

  return {
    isConnected,
    isListening,
    error,
    interimTranscript,
    finalTranscript,
    startListening,
    stopListening,
    resetTranscripts,
  };
}

/**
 * Higher-level hook for Avatar STT integration
 * Provides real-time transcripts from avatar/agent audio
 * Works alongside the AVATAR_TRANSCRIPTION event but provides real-time updates
 */
export function useDeepgramAvatarIntegration({
  onRealtimeTranscript,
  ...deepgramOptions
} = {}) {
  const [realtimeTranscript, setRealtimeTranscript] = useState("");

  const deepgram = useDeepgramAvatarSTT({
    ...deepgramOptions,
    onInterimTranscript: (transcript) => {
      setRealtimeTranscript(transcript);
      onRealtimeTranscript?.(transcript, false); // false = interim
    },
    onFinalTranscript: (transcript) => {
      setRealtimeTranscript(transcript);
      onRealtimeTranscript?.(transcript, true); // true = final
    },
  });

  return {
    ...deepgram,
    realtimeTranscript,
  };
}
